import { PinLog, useGetPinOptionsDropdownOptions } from "components";
import { pinsAttachments } from "components/PinModal/components/AddPinModalForm/utils/pinsAttachments";
import { createFormHelper } from "lib/Form";
import { PIN_ORDER_STATUSES } from "models";
import { PinOverview } from "modules/AppointmentDetails/components/Interventions/components";
import { PinCategoryForm } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components";
import "modules/AppointmentDetails/components/Interventions/components/PinCategory/PinCategory.scss";
import { usePinCategory } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/usePinCategory";
import { useAppointmentDealer } from "modules/AppointmentDetails/hooks";

import type { DropdownItemProps } from "components";
import type { Appointment, Intervention, PIN_TYPES, Pin } from "models";
import type { PinCategoryFormValues } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components";
import type { Dispatch, SetStateAction } from "react";

type PinCategoryProps = {
  selectedPinTypeOption: DropdownItemProps<PIN_TYPES>;
  intervention: Intervention;
  hasChanges: boolean;
  latestPinData?: Pin;
  appointment: Appointment;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
};

const PinOrderStatusColorMap = new Map<PIN_ORDER_STATUSES | null, string>([
  [PIN_ORDER_STATUSES.ToOrder, "blue"],
  [PIN_ORDER_STATUSES.SameDay, "green"],
  [PIN_ORDER_STATUSES.NextDay, "orange"],
  [PIN_ORDER_STATUSES.BackOrder, "red"],
  [PIN_ORDER_STATUSES.PartsArrived, "yellow"]
]);

export const getShopIconColor = (orderStatus: PIN_ORDER_STATUSES | null) => PinOrderStatusColorMap.get(orderStatus) ?? "";

const FormHelper = createFormHelper<PinCategoryFormValues>();

export const PinCategory = ({ intervention, appointment, latestPinData, selectedPinTypeOption, hasChanges, setHasChanges }: PinCategoryProps) => {
  const appointmentDealer = useAppointmentDealer(appointment);
  const { pinOrderOptions, pinStatusOptions, pinTypeOptions, pinWarrantyOptions } = useGetPinOptionsDropdownOptions(appointmentDealer);

  const { attachments, showPrintOverview, openModal, closeModal, handleSubmit, setAttachments } = usePinCategory({
    intervention,
    selectedPinTypeOption
  });

  const pinLogAttachments = pinsAttachments(intervention.pin_history);

  const defaultFormValues: PinCategoryFormValues = {
    claim_nr: latestPinData?.claim_nr || "",
    support_nr: latestPinData?.support_nr || "",
    ref_nr: latestPinData?.ref_nr || "",
    order_status: latestPinData?.order_status || null,
    warranty_type_id: latestPinData?.warranty_type_id || null,
    pin_status_id: latestPinData?.pin_status_id || null,
    note: "",
    visible_important_items: false,
    keep_parts: latestPinData?.keep_parts || false,
    note_suggestion: null
  };

  return (
    <div className="PinType">
      <FormHelper.Form onSubmit={handleSubmit} defaultValues={defaultFormValues}>
        <PinCategoryForm
          intervention={intervention}
          selectedPinTypeOption={selectedPinTypeOption}
          hasChanges={hasChanges}
          appointment={appointment}
          attachments={attachments}
          pinOrderOptions={pinOrderOptions}
          pinStatusOptions={pinStatusOptions}
          pinWarrantyOptions={pinWarrantyOptions}
          openModal={openModal}
          setHasChanges={setHasChanges}
          setAttachments={setAttachments}
        />
        <PinLog
          intervention={intervention}
          appointment={appointment}
          pinLog={intervention.pin_history}
          pinTypeOptions={pinTypeOptions}
          pinOrderOptions={pinOrderOptions}
          pinStatusOptions={pinStatusOptions}
          pinWarrantyOptions={pinWarrantyOptions}
          attachments={pinLogAttachments}
        />
        <PinOverview
          intervention={intervention}
          pin={latestPinData}
          onClose={closeModal}
          pinLog={intervention.pin_history}
          appointment={appointment}
          pinTypeOptions={pinTypeOptions}
          pinOrderOptions={pinOrderOptions}
          pinStatusOptions={pinStatusOptions}
          pinWarrantyOptions={pinWarrantyOptions}
          showPrintOverview={showPrintOverview}
        />
      </FormHelper.Form>
    </div>
  );
};
